// Shared types for Analyzely

// User types
export interface User {
  id: string;
  webflow_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  webflow_token?: string;
  is_designer?: boolean;
  created_at: string;
  updated_at: string;
}

// Billing types
export type FeatureFlag =
  | 'standard_reports'
  | 'email_support'
  | 'ecommerce_reports'
  | 'realtime_analytics'
  | 'custom_events'
  | 'priority_support'
  | 'white_label'
  | 'api_access'
  | 'team_collaboration';

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'cancelled' | 'incomplete';
export type BillingInterval = 'monthly' | 'yearly';

export interface Plan {
  id: string;
  name: string;
  monthly_price: number; // cents
  yearly_price: number;  // cents
  max_sites: number;     // -1 = unlimited
  data_retention_days: number;
  features: PlanFeature[];
  ecommerce_events: EcommerceEvent[];
  stripe_price_monthly?: string;
  stripe_price_yearly?: string;
  created_at: string;
  updated_at: string;
}

export interface Subscription {
  id: string;
  user_id: string;
  plan_id: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  status: SubscriptionStatus;
  billing_interval: BillingInterval;
  current_period_end?: string;
  trial_ends_at?: string;
  cancel_at_period_end: boolean;
  is_grandfathered: boolean;
  grandfathered_sites_count?: number;
  created_at: string;
  updated_at: string;
}

export interface Usage {
  id: string;
  user_id: string;
  sites_count: number;
  created_at: string;
  updated_at: string;
}

export interface BillingEvent {
  id: string;
  user_id?: string;
  event_type: string;
  event_data?: Record<string, unknown>;
  created_at: string;
}

export interface UsageLimits {
  sites: {
    used: number;
    limit: number;  // -1 = unlimited
    canAddMore: boolean;
  };
  dataRetentionDays: number;
  features: PlanFeature[];
}

// All possible plan features
export type PlanFeature =
  | 'standard_reports_basic'
  | 'standard_reports_full'
  | 'traffic_sources'
  | 'landing_exit_pages'
  | 'ecommerce_reports'
  | 'realtime_analytics'
  | 'hourly_heatmap'
  | 'period_comparison'
  | 'geographic_data'
  | 'device_analytics'
  | 'browser_os_analytics'
  | 'utm_tracking'
  | 'custom_date_range'
  | 'basic_charts'
  | 'all_charts'
  | 'pdf_export'
  | 'help_docs'
  | 'email_support';

// Feature display names for UI
export const FEATURE_DISPLAY_NAMES: Record<PlanFeature, string> = {
  standard_reports_basic: 'Basic Reports',
  standard_reports_full: 'Full Reports',
  traffic_sources: 'Traffic Sources',
  landing_exit_pages: 'Landing & Exit Pages',
  ecommerce_reports: 'E-commerce Reports',
  realtime_analytics: 'Realtime Analytics',
  hourly_heatmap: 'Hourly Heatmap',
  period_comparison: 'Period Comparison',
  geographic_data: 'Geographic Data',
  device_analytics: 'Device Analytics',
  browser_os_analytics: 'Browser/OS Analytics',
  utm_tracking: 'UTM Campaign Tracking',
  custom_date_range: 'Custom Date Range',
  basic_charts: 'Basic Charts',
  all_charts: 'All Charts',
  pdf_export: 'PDF Export',
  help_docs: 'Help Documentation',
  email_support: 'Email Support',
};

export interface SubscriptionWithPlan extends Subscription {
  plan: Plan;
}

// Site types
export interface Site {
  id: string;
  user_id: string;
  webflow_site_id: string;
  display_name: string;
  short_name: string;
  preview_url?: string;
  is_authorized: boolean;
  created_at: string;
  updated_at: string;
}

// Analytics configuration
export interface Analytics {
  id: string;
  site_id: string;
  google_account_id?: string;
  property_id?: string;
  measurement_id?: string;
  data_stream_name?: string;
  web_stream_id?: string;
  custom_code_site_id?: string;
  registered_script?: string;
  enabled: boolean;
  custom_code_applied: boolean;
  events: string[];
  api_secret?: string; // GA4 Measurement Protocol API secret (for server-side events)
  created_at: string;
  updated_at: string;
}

// E-commerce event types
export type EcommerceEvent =
  | 'purchase'
  | 'add_to_cart'
  | 'checkout'
  | 'view_item'
  | 'view_item_list';

export const ECOMMERCE_EVENTS: { id: EcommerceEvent; label: string; description: string }[] = [
  { id: 'purchase', label: 'Purchase', description: 'Track completed purchases (via webhook)' },
  { id: 'add_to_cart', label: 'Add to Cart', description: 'Track when items are added to cart' },
  { id: 'checkout', label: 'Begin Checkout', description: 'Track when checkout begins' },
  { id: 'view_item', label: 'View Item', description: 'Track product page views' },
  { id: 'view_item_list', label: 'View Item List', description: 'Track category page views' },
];

// Google Account
export interface GoogleAccount {
  id: string;
  user_id: string;
  google_email: string;
  refresh_token: string;
  created_at: string;
  updated_at: string;
}

// Google Login Data (for GA4 dashboard access)
export interface GoogleLoginData {
  id: string;
  site_id: string;
  google_email: string;
  refresh_token: string;
  created_at: string;
  updated_at: string;
}

// API Response types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Report types
export interface StandardReport {
  sessions: number;
  users: number;
  pageViews: number;
  bounceRate: number;
  avgSessionDuration: number;
  newUsers: number;
  sessionsPerUser: number;
  engagedSessions: number;
  sessionsOverTime: TimeSeriesData[];
  topPages: DimensionData[];
  topReferrers: DimensionData[];
  topCountries: DimensionData[];
  deviceCategories: DimensionData[];
  browsers: DimensionData[];
  operatingSystems: DimensionData[];
  // Comparison data
  previousPeriod?: {
    sessions: number;
    users: number;
    pageViews: number;
    bounceRate: number;
    avgSessionDuration: number;
    sessionsPerUser: number;
    engagedSessions: number;
    sessionsOverTime?: TimeSeriesData[];
  };
}

export interface RealtimeReport {
  activeUsers: number;
  pageViews: number;
  events: number;
  topPages: DimensionData[];
  topCountries: DimensionData[];
  deviceCategories: DimensionData[];
}

// E-commerce Report types
export interface EcommerceReport {
  // Main KPIs
  totalRevenue: number;
  transactions: number;
  averageOrderValue: number;
  conversionRate: number;

  // Additional metrics
  addToCarts: number;
  checkouts: number;
  itemViews: number;
  cartAbandonmentRate: number;

  // Sessions for conversion calculation
  sessions: number;

  // Time series
  revenueOverTime: TimeSeriesData[];

  // Dimension data
  topProducts: ProductData[];

  // Previous period for comparison
  previousPeriod?: {
    totalRevenue: number;
    transactions: number;
    averageOrderValue: number;
    addToCarts: number;
    checkouts: number;
    itemViews: number;
    sessions: number;
  };

  // Currency
  currency: string;
}

export interface ProductData {
  name: string;
  revenue: number;
  quantity: number;
  percentage: number;
}

// Traffic Sources Report types
export interface TrafficSourcesReport {
  bySource: TrafficSourceData[];
  byMedium: DimensionData[];
  campaigns: CampaignData[];
  totalSessions: number;
  previousPeriod?: {
    totalSessions: number;
  };
}

export interface TrafficSourceData {
  source: string;
  sessions: number;
  users: number;
  bounceRate: number;
  percentage: number;
}

export interface CampaignData {
  campaign: string;
  sessions: number;
  users: number;
  bounceRate: number;
  percentage: number;
}

// Hourly Analysis Report types
export interface HourlyAnalysisReport {
  heatmapData: HeatmapCell[][];
  peakHour: { hour: number; dayOfWeek: number; value: number };
  busiestDay: { dayOfWeek: number; totalValue: number; dayName: string };
  totalSessions: number;
}

export interface HeatmapCell {
  dayOfWeek: number;
  hour: number;
  value: number;
  intensity: number; // 0-1 normalized value for color intensity
}

// Landing & Exit Pages Report types
export interface PagesAnalysisReport {
  landingPages: LandingPageData[];
  exitPages: ExitPageData[];
  totalSessions: number;
  totalExits: number;
  previousPeriod?: {
    totalSessions: number;
    totalExits: number;
  };
}

export interface LandingPageData {
  page: string;
  sessions: number;
  users: number;
  bounceRate: number;
  avgSessionDuration: number;
  percentage: number;
}

export interface ExitPageData {
  page: string;
  exits: number;
  pageViews: number;
  exitRate: number;
  percentage: number;
}

export interface TimeSeriesData {
  date: string;
  value: number;
}

export interface DimensionData {
  dimension: string;
  value: number;
  percentage?: number;
}

// Webflow types (API v2 uses 'id', not '_id')
export interface WebflowSite {
  id: string;
  displayName: string;
  shortName: string;
  previewUrl?: string;
  customDomains?: string[];
  locales?: { primary: { tag: string } };
  createdOn?: string;
  lastPublished?: string;
}

export interface WebflowUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  status?: string;
}

// GA4 types
export interface GA4Account {
  name: string;
  displayName: string;
}

export interface GA4Property {
  name: string;
  displayName: string;
  propertyType: string;
  parent: string;
}

export interface GA4DataStream {
  name: string;
  displayName: string;
  webStreamData?: {
    measurementId: string;
    defaultUri: string;
  };
}

// Date range options
export type DateRangeOption =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last28days'
  | 'last90days'
  | 'last12months'
  | 'custom';

export interface DateRange {
  startDate: string;
  endDate: string;
}

// Webhook types
export interface WebhookPayload {
  triggerType: string;
  payload: Record<string, unknown>;
  site: string;
}

// Email queue types
export interface EmailJob {
  type: 'magic-link' | 'welcome' | 'uninstall' | 'notification';
  to: string;
  subject: string;
  data: Record<string, unknown>;
}

// Constants
export const DATE_RANGES: Record<DateRangeOption, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7days: 'Last 7 days',
  last28days: 'Last 28 days',
  last90days: 'Last 90 days',
  last12months: 'Last 12 months',
  custom: 'Custom',
};

// Utility functions
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString();
}

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}m ${secs}s`;
}

export function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export function calculateChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}
